import * as React from 'react';
import { Box, Tooltip } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { keyboardActivationProps } from '../common/keyboardActivation';
import { RAIL } from './railTokens';

const { tooltip: TIP, modelLinks: M } = RAIL;

/**
 * One row of the side rail. A `role="button"` host rather than a native `<button>` because the
 * tree's keyboard-activation sweep pins the native-button count exactly, and this is the house shape
 * for a navigation row (see `keyboardActivation.ts`).
 *
 * The label is always in the DOM and fades with the rail. While the rail is closed the row carries
 * the label as its `title` and `aria-label`, and a tooltip beside it shows the same text; open, the
 * label is on screen and the row carries no `title` (handoff section 4, "Title attribute").
 *
 * Two shapes. `global` is the 44px row with its own highlight and gold marker. `model` is the 40px
 * link under the model name: it has no fill when active, and the group's one gold segment glides
 * between links, so a model link paints no marker. `parent` is a global row whose destination
 * contains the current page (Models, while inside a model): emphasised, not highlighted, not
 * `aria-current`, and its icon keeps the idle stroke.
 */
export function RailRow(props: {
  icon: React.ReactNode;
  label: string;
  open: boolean;
  active?: boolean;
  parent?: boolean;
  variant?: 'global' | 'model';
  onActivate: () => void;
  sx?: SxProps<Theme>;
}) {
  const { icon, label, open, active = false, parent = false, variant = 'global', onActivate, sx } = props;
  const isModel = variant === 'model';
  const lit = active || parent;
  return (
    <Tooltip
      title={open ? '' : (
        <>
          {label}
          {/* The 8x8 square, rotated 45deg, whose left and bottom edges draw the pointer. */}
          <Box
            component="i"
            aria-hidden
            sx={{
              position: 'absolute',
              left: '-5px',
              top: '50%',
              width: 8,
              height: 8,
              bgcolor: '#fff',
              borderLeft: '1px solid #ededed',
              borderBottom: '1px solid #ededed',
              transform: 'translateY(-50%) rotate(45deg)',
            }}
          />
        </>
      )}
      placement="right"
      enterDelay={0}
      leaveDelay={0}
      disableInteractive
      TransitionProps={{ timeout: 0 }}
      slotProps={{
        popper: { modifiers: [{ name: 'offset', options: { offset: [0, TIP.gap] } }] },
        tooltip: {
          sx: {
            position: 'relative',
            bgcolor: '#fff',
            color: '#1a1a1a',
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: '-0.1px',
            lineHeight: 'normal',
            p: '8px 12px',
            m: 0,
            maxWidth: 'none',
            whiteSpace: 'nowrap',
            borderRadius: '10px',
            border: TIP.border,
            boxShadow: TIP.shadow,
            '.MuiTooltip-popper[data-popper-placement*="right"] &': { ml: 0 },
          },
        },
      }}
    >
    <Box
      {...keyboardActivationProps(onActivate)}
      onClick={onActivate}
      title={open ? undefined : label}
      aria-label={open ? undefined : label}
      aria-current={active ? 'page' : undefined}
      sx={[
        {
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          height: isModel ? M.linkHeight : RAIL.rowHeight,
          p: '0 14px',
          borderRadius: '10px',
          cursor: 'pointer',
          flex: 'none',
          whiteSpace: 'nowrap',
          fontSize: 14,
          letterSpacing: '-0.1px',
          fontWeight: lit ? 600 : 500,
          color: lit ? '#fff' : isModel ? M.idle : RAIL.idle,
          background: active && !isModel ? RAIL.activeFill : 'transparent',
          transition: RAIL.rowTransition,
          '&:hover': {
            color: '#fff',
            background: isModel
              ? (active ? 'transparent' : M.hoverFill)
              : active
                ? RAIL.activeFillHover
                : RAIL.hoverFill,
          },
          // A model link sits flush in an `overflow: hidden` group, so an outside ring would be clipped.
          ...(isModel ? { '&:focus-visible': { outlineOffset: '-4px' } } : null),
          '& svg': { fontSize: 20, flex: 'none', strokeWidth: active ? 2 : 1.8 },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {active && !isModel && (
        <Box
          component="span"
          aria-hidden
          sx={{
            position: 'absolute',
            left: '-12px',
            top: '10px',
            bottom: '10px',
            width: '3px',
            borderRadius: '0 3px 3px 0',
            background: 'var(--hg)',
            boxShadow: RAIL.markShadow,
          }}
        />
      )}
      {icon}
      <Box
        component="span"
        sx={{
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          opacity: open ? 1 : 0,
          maxWidth: open ? 200 : 0,
          transition: RAIL.fadeTransition,
        }}
      >
        {label}
      </Box>
    </Box>
    </Tooltip>
  );
}
